import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import "@/App.css";
import { SITE } from "@/config/site";
import { Header } from "@/components/vmemoo/Header";
import { AppPromotionBanner } from "@/components/vmemoo/AppPromotionBanner";
import { Footer } from "@/components/vmemoo/Footer";
import { LegalPage } from "@/components/vmemoo/LegalPage";
import { HomePage } from "@/pages/HomePage";
import { ContactPage } from "@/pages/ContactPage";
import { VerifyEmailPage } from "@/pages/VerifyEmailPage";
import { PasswordResetPage } from "@/pages/PasswordResetPage";
import UpdatesPage from "@/pages/UpdatesPage";
import MilestoneDetailPage from "@/pages/MilestoneDetailPage";
import { legalDocuments } from "@/content/legal";
import { milestones } from "@/data/milestones";

function PageMeta({ title, description }) {
  useEffect(() => {
    document.title = title;
    document.querySelector('meta[name="description"]')?.setAttribute("content", description);
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = `${window.location.origin}${window.location.pathname}`;
    document.querySelector('meta[property="og:title"]')?.setAttribute("content", title);
    document.querySelector('meta[property="og:description"]')?.setAttribute("content", description);
    document.querySelector('meta[property="og:url"]')?.setAttribute("content", canonical.href);
    document.querySelector('meta[name="twitter:title"]')?.setAttribute("content", title);
    document.querySelector('meta[name="twitter:description"]')?.setAttribute("content", description);
  }, [title, description]);
  return null;
}

function ScrollReset() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  return null;
}

function Shell({ children }) {
  return <div className="site-shell"><AppPromotionBanner /><Header />{children}<Footer /></div>;
}

function DocumentRoute({ document }) {
  return <><PageMeta title={`${document.title} — Vmemoo`} description={`${document.title} for Vmemoo Limited. Version ${document.version}, effective ${document.effectiveDate}.`} /><LegalPage document={document} /></>;
}

function NotFound() {
  return <main className="not-found"><PageMeta title="Page not found — Vmemoo" description={SITE.description} /><p className="eyebrow">404 / Not found</p><h1>This page wandered off.</h1><p>Return to Vmemoo and find a place for the stories that stay with us.</p><a className="button button-dark" href="/">Back home</a></main>;
}

function RoutedApp() {
  return (
    <>
      <ScrollReset />
      <Shell>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/verify" element={<VerifyEmailPage />} />
          <Route path="/reset-password" element={<PasswordResetPage />} />
          <Route path="/updates" element={<><PageMeta title="Vmemoo Updates | Milestones & What's Next" description="Follow Vmemoo's development journey, completed milestones, current work and what's planned next." /><UpdatesPage /></>} />
          <Route path="/updates/:slug" element={<MilestoneRoute />} />
          <Route path="/privacy" element={<DocumentRoute document={legalDocuments.privacy} />} />
          <Route path="/terms" element={<DocumentRoute document={legalDocuments.terms} />} />
          <Route path="/community-guidelines" element={<DocumentRoute document={legalDocuments.community} />} />
          <Route path="/cookie-data-use" element={<DocumentRoute document={legalDocuments.cookie} />} />
          <Route path="/memorial-policy" element={<DocumentRoute document={legalDocuments.memorial} />} />
          <Route path="/official-memorial-policy" element={<DocumentRoute document={legalDocuments.officialMemorial} />} />
          <Route path="/copyright" element={<DocumentRoute document={legalDocuments.copyright} />} />
          <Route path="/safety" element={<DocumentRoute document={legalDocuments.safety} />} />
          <Route path="/account-deletion" element={<DocumentRoute document={legalDocuments.accountDeletion} />} />
          <Route path="/privacy-request" element={<DocumentRoute document={legalDocuments.privacyRequest} />} />
          <Route path="/404" element={<NotFound />} />
          <Route path="*" element={<Navigate to="/404" replace />} />
        </Routes>
      </Shell>
    </>
  );
}

function MilestoneRoute() {
  const slug = window.location.pathname.split("/").pop();
  const milestone = milestones.find((entry) => entry.slug === slug);
  return <MilestoneDetailPage pageMeta={milestone && <PageMeta title={`${milestone.title} — Vmemoo Updates`} description={milestone.shortDescription} />} />;
}

export default function App() {
  return <BrowserRouter><RoutedApp /></BrowserRouter>;
}
